import { Typography, Collapse, Tabs } from "antd";
import { ArrowRight, ArrowDown } from "lucide-react";
import { useState } from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { useLanguage } from "../../../context/LanguageContext";
import content from "../../../utils/content";

const { Title, Text } = Typography;
const { Panel } = Collapse;

export function FAQSection() {
  const { language } = useLanguage();
  const t = content[language].faq;

  const [activeCategory, setActiveCategory] = useState("general");

  // Intersection Observer
  const [titleRef, titleInView] = useInView({
    triggerOnce: true,
    threshold: 0.2,
  });
  const [contentRef, contentInView] = useInView({
    triggerOnce: true,
    threshold: 0.2,
  });

  const fadeInUp = (delay = 0) => ({
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, delay } },
  });

  return (
    <div className="w-full px-4 md:px-6 py-12 md:py-24" id="FAQ">
      {/* Titel Animation */}
      <motion.div
        ref={titleRef}
        initial="hidden"
        animate={titleInView ? "visible" : "hidden"}
        variants={fadeInUp(0)}
      >
        <Title
          level={2}
          className="lg:!text-[5rem] !text-4xl font-bold tracking-tight text-center !mb-24 lg:!leading-[5rem]"
        >
          {t.title}
        </Title>
      </motion.div>

      <motion.div
        ref={contentRef}
        initial="hidden"
        animate={contentInView ? "visible" : "hidden"}
        variants={{
          visible: {
            transition: {
              staggerChildren: 0.2,
            },
          },
        }}
        className="lg:grid flex flex-col gap-8 lg:grid-cols-[40%_60%]"
      >
        {/* Kategorien (Links) */}
        <motion.div variants={fadeInUp(0.2)} className="space-y-4 max-w-[100%] custom-scrollbar">
          <div className="border rounded-lg w-full h-full lg:flex lg:flex-col hidden custom-scrollbar">
            {t.categories.map((category) => (
              <div
                key={category.key}
                className={`flex items-center !h-[auto] px-4 py-4 hover:bg-gray-100 cursor-pointer ${
                  activeCategory === category.key
                    ? "bg-[#FF7051]/10 !text-[#FF7051]"
                    : ""
                }`}
                onClick={() => setActiveCategory(category.key)}
              >
                <span className="flex items-center justify-center w-8 h-8 text-center bg-[#FF7051] text-white rounded-lg">
                  {category.number}
                </span>
                <div className="flex flex-col max-w-[100%] ml-4">
                  <h5 className="!mb-1">{category.title}</h5>
                  <p className="text-gray-500 whitespace-normal break-words">
                    {category.description}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Mobile Tabs */}
          <div
            style={{ maxWidth: "100%", overflowX: "auto" }}
            className="block lg:hidden custom-scrollbar"
          >
            <Tabs
              activeKey={activeCategory}
              onChange={setActiveCategory}
              type="line"
              tabBarGutter={0}
              moreIcon={null}
              renderTabBar={(props, DefaultTabBar) => (
                <DefaultTabBar
                  {...props}
                  style={{
                    display: "flex",
                    overflowX: "auto",
                    width: "100%",
                  }}
                />
              )}
            >
              {t.categories.map((category) => (
                <Tabs.TabPane tab={category.title} key={category.key} />
              ))}
            </Tabs>
          </div>
        </motion.div>

        {/* FAQ Content */}
        <motion.div variants={fadeInUp(0.4)}>
          <Collapse
            expandIcon={({ isActive }) =>
              isActive ? (
                <ArrowDown className="text-[#FF7051]" />
              ) : (
                <ArrowRight className="text-[#FF7051]" />
              )
            }
            ghost
            className="bg-white rounded-lg [&_.ant-collapse-header]:py-4 space-y-12 lg:divide-y-2"
          >
            {t.categories
              .find((section) => section.key === activeCategory)
              .questions.map((faq, idx) => (
                <Panel header={faq.q} key={idx}>
                  <Text>{faq.a}</Text>
                </Panel>
              ))}
          </Collapse>
        </motion.div>
      </motion.div>
    </div>
  );
}
